import { ChannelData, MediaCount } from "../types";
import Card from "./card/Card";
import { useCallback, useEffect, useRef, useState } from "react";
import { baseUrl, postKmsData, useMediaQuery, translate } from "@kaltura/mediaspace-shared-utils";
import { StyledSearchResultsContainer, StyledGridRow } from "../styled-components";
import { useTheme } from "@kaltura/mediaspace-shared-styled";

export interface CardsContainerProps {
    /**
     * channels data to render
     */
    channels: ChannelData[];
}

const CardsContainer = (props: CardsContainerProps) => {
    const { channels } = props;

    const [mediaCount, setMediaCount] = useState<MediaCount>({});

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const isTablet = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    // Responsive grid layout: 1 column on mobile, 2 on tablet, 3 on desktop
    const columnsCount = isMobile ? 1 : isTablet ? 2 : 3;

    const makeRequest = useCallback(async () => {
        try {
            return await postKmsData(
                `${baseUrl}/default/channels/get-media-count`,
                { channelIds: channels.map((channel) => channel.id) }
            );
        }
        catch (e) {
            console.debug("default/channels/get-media-count failed", e);
            return undefined;
        }
    }, [channels]);

    useEffect(() => {
        // fetch media count data from kms
        (async () => {
            const response = await makeRequest();
            if (response?.mediaCount) {
                setMediaCount(response.mediaCount);
            }
        })();
    }, [makeRequest]);

    const gridRef = useRef<HTMLDivElement>(null);
    const gridItemRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Break channels into rows
    const rows: ChannelData[][] = [];
    for (let i = 0; i < channels.length; i += columnsCount) {
        rows.push(channels.slice(i, i + columnsCount));
    }

    // Clear refs array when channels change to prevent stale references
    useEffect(() => {
        gridItemRefs.current = new Array(channels.length).fill(null);
    }, [channels.length]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const gridItems = gridItemRefs.current;
            const activeElement = document.activeElement as HTMLElement;
            const currentIndex = gridItems.findIndex((item) => item && item.contains(activeElement));

            if (currentIndex === -1) {
                // ignore if focus is outside grid
                return;
            }

            if (e.key === "ArrowRight") {
                e.preventDefault();
                // Move one cell to the right, wrapping to next row if needed
                if (currentIndex < gridItems.length - 1) {
                    gridItems[currentIndex + 1]?.focus();
                }
            }
            else if (e.key === "ArrowLeft") {
                e.preventDefault();
                // Move one cell to the left, wrapping to previous row if needed
                if (currentIndex > 0) {
                    gridItems[currentIndex - 1]?.focus();
                }
            }
            else if (e.key === "ArrowDown") {
                e.preventDefault();
                // Move to next row, same column
                const nextRowIndex = currentIndex + columnsCount;
                if (nextRowIndex < gridItems.length) {
                    gridItems[nextRowIndex]?.focus();
                }
            }
            else if (e.key === "ArrowUp") {
                e.preventDefault();
                // Move to previous row, same column
                const prevRowIndex = currentIndex - columnsCount;
                if (prevRowIndex >= 0) {
                    gridItems[prevRowIndex]?.focus();
                }
            }
            else if (e.key === "Home") {
                e.preventDefault();
                if (e.ctrlKey) {
                    // Ctrl+Home: Move to first cell in grid
                    gridItems[0]?.focus();
                }
                else {
                    // Home: Move to first cell in current row
                    const currentRow = Math.floor(currentIndex / columnsCount);
                    const rowStart = currentRow * columnsCount;
                    gridItems[rowStart]?.focus();
                }
            }
            else if (e.key === "End") {
                e.preventDefault();
                if (e.ctrlKey) {
                    // Ctrl+End: Move to last cell in grid
                    gridItems[gridItems.length - 1]?.focus();
                }
                else {
                    // End: Move to last cell in current row
                    const currentRow = Math.floor(currentIndex / columnsCount);
                    const rowStart = currentRow * columnsCount;
                    const rowEnd = Math.min(rowStart + columnsCount - 1, gridItems.length - 1);
                    gridItems[rowEnd]?.focus();
                }
            }
        };

        const grid = gridRef.current;
        grid?.addEventListener("keydown", handleKeyDown);

        return () => grid?.removeEventListener("keydown", handleKeyDown);
    }, [columnsCount]);

    return (
        <StyledSearchResultsContainer
            ref={gridRef}
            role={"grid"}
            aria-rowcount={rows.length}
            aria-colcount={columnsCount}
            aria-label={translate("Channels container")}
        >
            {rows.map((row, rowIndex) => (
                <StyledGridRow key={rowIndex} role={"row"} aria-rowindex={rowIndex + 1}>
                    {row.map((channel, colIndex) => {
                        const flatIndex = rowIndex * columnsCount + colIndex;
                        return (
                            <Card
                                key={channel.id}
                                {...channel}
                                mediaCount={mediaCount[channel.id]}
                                ref={(el) => {
                                    gridItemRefs.current[flatIndex] = el;
                                }}
                                ariaColIndex={colIndex + 1}
                            />
                        );
                    })}
                </StyledGridRow>
            ))}
        </StyledSearchResultsContainer>
    );
};

export default CardsContainer;
